import React, { useCallback, useMemo, useState } from "react";
import ProgramasNavigation from "../../components/ProgramasNavigation";
import CampaignProgressCard from "../../components/CampaignProgressCard";
import TopMetasNegociadas from "../../components/TopMetasNegociadas";
import TopProximosMeta from "../../components/TopProximosMeta";
import LinhaDoTempo from "../../components/LinhaDoTempo";
import BateuLevouListing from "../../components/BateuLevouListing";
import FilterSelect from "../../components/FilterSelect";
import { useBateuLevouData, montarLinhaTempo, filtrarRowsPorProduto } from "../../hooks/useBateuLevouData";
import { IconSearch, SearchUser } from "~misc/icons";

const aceiteOptions = [
  { value: "", label: "Todos" },
  { value: "sim", label: "SIM" },
  { value: "nao", label: "NÃO" },
];

const BateuLevouPage = () => {
  const [activePeriod, setActivePeriod] = useState("current");
  const [searchValue, setSearchValue] = useState("");
  const [grupoProduto, setGrupoProduto] = useState("");
  const [grupoCentralizador, setGrupoCentralizador] = useState("");
  const [aceite, setAceite] = useState("");

  const { rows, mesAtual, mesAnterior, periodoAtual, periodoAnterior, topMetasNegociadas, topMetasNegociadasAnterior, topProximosMeta, topProximosMetaAnterior, linhaTempoDescontoRaw, linhaTempoVolumeRaw, filtros, updatedAt, isLoading, isRefreshing } = useBateuLevouData();

  const periodoCards = activePeriod === "current" ? periodoAtual : periodoAnterior;
  const mesReferencia = activePeriod === "current" ? mesAtual : mesAnterior;

  const metasNegociadas = activePeriod === "current" ? topMetasNegociadas : topMetasNegociadasAnterior;
  const proximosMeta    = activePeriod === "current" ? topProximosMeta    : topProximosMetaAnterior;

  const filteredRows = useMemo(() => {
    let result = mesReferencia ? rows.filter((r) => !r.mesAno || r.mesAno === mesReferencia) : rows;
    if (searchValue.trim()) {
      const term = searchValue.trim().toLowerCase();
      result = result.filter(
        (r) =>
          r.cliente.toLowerCase().includes(term) ||
          r.grupoCentralizador.toLowerCase().includes(term)
      );
    }
    if (grupoCentralizador) {
      result = result.filter((r) => r.grupoCentralizador === grupoCentralizador);
    }
    if (grupoProduto) {
      result = filtrarRowsPorProduto(result, grupoProduto);
    }
    if (aceite === "sim") result = result.filter((r) => r.aceite === true);
    if (aceite === "nao") result = result.filter((r) => r.aceite !== true);
    return result;
  }, [rows, mesReferencia, searchValue, grupoCentralizador, grupoProduto, aceite]);

  const filteredLinhaTempo = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    const filterItems = (items) => {
      let result = mesReferencia
        ? items.filter((r) => !r.inicioMes || String(r.inicioMes).slice(0, 7) <= mesReferencia)
        : items;
      if (grupoCentralizador) result = result.filter((r) => r.grupoCentralizador === grupoCentralizador);
      if (grupoProduto)       result = result.filter((r) => r.produto === grupoProduto);
      if (aceite === "sim")   result = result.filter((r) => r.aceite === true);
      if (aceite === "nao")   result = result.filter((r) => r.aceite !== true);
      if (term) result = result.filter((r) =>
        `${r.codigo} - ${r.razaoSocial}`.toLowerCase().includes(term) ||
        (r.grupoCentralizador ?? "").toLowerCase().includes(term)
      );
      return result;
    };
    return montarLinhaTempo(filterItems(linhaTempoDescontoRaw), filterItems(linhaTempoVolumeRaw));
  }, [linhaTempoDescontoRaw, linhaTempoVolumeRaw, mesReferencia, grupoCentralizador, grupoProduto, aceite, searchValue]);

  const hasFilter = searchValue.trim() !== "" || grupoCentralizador !== "" || grupoProduto !== "" || aceite !== "";
  const semCardsCampanha = !hasFilter && periodoCards.semDados;

  const semResultados = hasFilter && filteredRows.length === 0;

  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    setGrupoProduto("");
    setGrupoCentralizador("");
    setAceite("");
  }, []);

  return (
    <>
      {isLoading && !rows?.length ? (
        <div className="flex h-64 items-center justify-center">
          <span className="text-[#1A2B49] font-semibold">Carregando...</span>
        </div>
      ) : (
      <>
      {isRefreshing && (
        <div className="container">
          <div className="w-[130px] flex mt-4 items-center gap-2 bg-[#01054C] rounded-md px-4 py-2 text-xs font-semibold text-[#fff] animate-pulse">
            <span className="rounded-full" />
            Atualizando...
          </div>
        </div>
      )}
      <ProgramasNavigation>
        <CampaignProgressCard
          title="Bateu Levou"
          period={periodoCards.period}
          activePeriod={activePeriod}
          onPeriodChange={setActivePeriod}
          statCards={hasFilter || semCardsCampanha ? [] : periodoCards.statCards}
          progressItems={hasFilter || semCardsCampanha ? [] : periodoCards.progressItems}
          emptyMessage={semCardsCampanha ? "Sem dados de campanha para este período." : null}
        >
            <div className="flex flex-col gap-3">
              <div className="relative flex items-center">
                <SearchUser className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <input
                  value={searchValue}
                  placeholder="Busque por Código de Cliente, Razão Social ou Número do Pedido"
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="h-11 w-full rounded-md border border-[#C8CED8] bg-white pl-10 pr-11 text-sm text-[#1A2B49] outline-none"
                />
                <IconSearch className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FilterSelect
                  label="Grupo de Produto"
                  value={grupoProduto}
                  onChange={setGrupoProduto}
                  options={filtros.grupoProdutoOptions}
                  className="flex flex-col gap-1"
                />
                <FilterSelect
                  label="Grupo Centralizador:"
                  value={grupoCentralizador}
                  onChange={setGrupoCentralizador}
                  options={filtros.grupoCentralizadorOptions}
                  className="flex flex-col gap-1"
                />
                <FilterSelect
                  label="Deu Aceite:"
                  value={aceite}
                  onChange={setAceite}
                  options={aceiteOptions}
                  className="flex flex-col gap-1"
                />
              </div>
              {hasFilter && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="rounded-md border border-[#0655A3] bg-white px-3 py-1.5 text-xs font-semibold text-[#0655A3] hover:bg-[#0655A3] hover:text-white transition-colors"
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
          </CampaignProgressCard>
        </ProgramasNavigation>

      {!hasFilter && (
        <div className="container flex flex-col lg:flex-row gap-4 mt-4 ">
          <TopMetasNegociadas labels={metasNegociadas.labels} data={metasNegociadas.data} />
          <TopProximosMeta
            labels={proximosMeta.labels}
            atingidoData={proximosMeta.atingidoData}
            gapData={proximosMeta.gapData}
          />
        </div>
      )}

      {semResultados ? (
        <div className="container mt-4">
          <div className="flex h-40 items-center justify-center rounded-lg bg-white text-[#1A2B49] font-semibold" style={{ boxShadow: "0 1px 6px 0 rgba(0,0,0,0.10)" }}>
            Não existem informações para esse filtro
          </div>
        </div>
      ) : (
        <>
          {filteredLinhaTempo.descontoLabels.length > 0 && (
            <div className="container mt-4">
              <LinhaDoTempo
                descontoLabels={filteredLinhaTempo.descontoLabels}
                descontoRealizado={filteredLinhaTempo.descontoRealizado}
                descontoGap={filteredLinhaTempo.descontoGap}
                volumeLabels={filteredLinhaTempo.volumeLabels}
                volumeRealizado={filteredLinhaTempo.volumeRealizado}
                volumeGap={filteredLinhaTempo.volumeGap}
                inicioMesIndex={filteredLinhaTempo.inicioMesIndex}
              />
            </div>
          )}

          <div className="mt-4">
            <BateuLevouListing rows={filteredRows} updatedAt={updatedAt} />
          </div>
        </>
      )}
      </>
      )}
    </>
  );
};

export default BateuLevouPage;
